import { Fragment } from 'react'
import type { IrrigationScheduleConfig } from '@/lib/api/irrigation-devices-api'
import { useIrrigationSchedulePreview } from '@/hooks/use-irrigation-schedule-query'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { formatTimeVN, groupSessionsByDay } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'

/**
 * Các phiên 7 ngày tới của cấu hình đang nhập — do backend tính, nên xem
 * trước luôn khớp với cái bộ chạy lịch sẽ làm. configJson null = form chưa
 * đủ để xem trước.
 */
export function IrrigationSchedulePreview({
    configJson,
}: {
    configJson: string | null
}) {
    const config = configJson
        ? (JSON.parse(configJson) as IrrigationScheduleConfig)
        : null
    const { data, error } = useIrrigationSchedulePreview(config)
    const sessions = data?.sessions ?? []
    const warnings = data?.warnings ?? []

    return (
        <section
            className="space-y-2"
            aria-label={m.irrigation_schedule_preview_title()}
        >
            <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium">
                    {m.irrigation_schedule_preview_title()}
                </h3>
                {config && !error && (
                    <span className="text-xs text-muted-foreground">
                        {m.irrigation_schedule_preview_count({
                            count: sessions.length,
                        })}
                    </span>
                )}
            </div>
            {config && !error && warnings.length > 0 && (
                <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
                    {warnings.map((code) => (
                        <li key={code}>{warningText(code)}</li>
                    ))}
                </ul>
            )}
            {!config ? (
                <p className="text-sm text-muted-foreground">
                    {m.irrigation_schedule_preview_fix()}
                </p>
            ) : error ? (
                <p className="text-sm text-destructive">
                    {/* Lỗi dữ liệu (IRRIGATION_SCHEDULE_*) hiện câu của ô sai; lỗi
                        khác (404, mất mạng, 500) không phải lỗi của người
                        nhập nên không bảo họ sửa ô. */}
                    {(error instanceof ApiError &&
                        resolveApiErrorMessage(error.code)) ||
                        m.irrigation_schedule_preview_error()}
                </p>
            ) : sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {m.irrigation_schedule_preview_empty()}
                </p>
            ) : (
                <ul className="max-h-56 space-y-1.5 overflow-y-auto rounded-md border p-2 text-sm">
                    {groupSessionsByDay(sessions).map((day) => (
                        <li key={day.label} className="flex gap-3">
                            <span className="w-16 shrink-0 font-medium">
                                {day.label}
                            </span>
                            {/* Mỗi khoảng giờ không bị ngắt giữa chừng: xuống dòng
                                chỉ ở giữa hai khoảng. */}
                            <span className="min-w-0 tabular-nums text-muted-foreground">
                                {day.sessions.map((s, i) => (
                                    <Fragment key={s.start}>
                                        {i > 0 && ', '}
                                        <span className="whitespace-nowrap">
                                            {`${formatTimeVN(s.start)}–${formatTimeVN(s.end)}`}
                                        </span>
                                    </Fragment>
                                ))}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}

/** Cảnh báo do backend trả (mã), FE dịch. Mã lạ thì hiện nguyên mã. */
function warningText(code: string): string {
    if (code === 'IRRIGATION_SCHEDULE_TIGHT_GAP') {
        return m.irrigation_schedule_warning_tight_gap()
    }
    return code
}
